
import React, { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || '/.netlify/functions/api';

function getCsrfToken() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
    return m ? decodeURIComponent(m[1]) : '';
  } catch {
    return '';
  }
}

const CATEGORY_OPTIONS = [
  { value: 'lecturer', label: 'بلاغ عن محاضر' },
  { value: 'course',   label: 'بلاغ عن مقرر'  },
  { value: 'other',    label: 'مشكلة أخرى'     },
];

const SEVERITY_OPTIONS = [
  { value: 'high',   label: 'عالي',    desc: 'يؤثر بشكل كبير على الدراسة',  dot: '#f87171' },
  { value: 'medium', label: 'متوسط',   desc: 'مشكلة تحتاج متابعة',           dot: '#fcd34d' },
  { value: 'low',    label: 'منخفض',   desc: 'ملاحظة أو اقتراح',             dot: '#93c5fd' },
];

function validate(category, targetName, title, description) {
  if (!title.trim())                   return 'عنوان المشكلة مطلوب.';
  if (title.trim().length < 5)         return 'العنوان قصير جداً (5 أحرف على الأقل).';
  if (!description.trim())             return 'وصف المشكلة مطلوب.';
  if (description.trim().length < 20)  return 'الوصف قصير جداً (20 حرفاً على الأقل).';
  return null;
}

export default function ReportWidget() {
  const { isAuthenticated } = useAuth();

  const [open, setOpen]             = useState(false);
  const [step, setStep]             = useState('form');

  const [category,    setCategory]    = useState('other');
  const [targetName,  setTargetName]  = useState('');
  const [title,       setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [severity,    setSeverity]    = useState('medium');

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg,   setErrorMsg]   = useState('');

  const reset = () => {
    setStep('form');
    setCategory('other');
    setTargetName('');
    setTitle('');
    setDescription('');
    setSeverity('medium');
    setErrorMsg('');
  };

  const openModal  = () => { reset(); setOpen(true); };
  const closeModal = () => { if (submitting) return; setOpen(false); };

  const submit = useCallback(async () => {
    const err = validate(category, targetName, title, description);
    if (err) { setErrorMsg(err); return; }

    setSubmitting(true);
    setErrorMsg('');

    try {
      const csrf = getCsrfToken();
      const headers = { 'Content-Type': 'application/json' };
      if (csrf) headers['X-XSRF-TOKEN'] = csrf;

      const resp = await fetch(`${API_BASE}/reports`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          category,
          targetName:  targetName.trim().slice(0, 200),
          title:       title.trim().slice(0, 200),
          description: description.trim().slice(0, 2000),
          severity,
          source: typeof window !== 'undefined' ? window.location.pathname : '',
        }),
      });

      if (resp.status === 429) {
        setErrorMsg('أرسلت بلاغات كثيرة، انتظر قليلاً ثم حاول مرة أخرى.');
        return;
      }
      if (resp.status === 401) {
        setErrorMsg('يجب تسجيل الدخول أولاً لإرسال بلاغ.');
        return;
      }
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        setErrorMsg(j?.error || 'حدث خطأ أثناء الإرسال، حاول مرة أخرى.');
        return;
      }

      setStep('success');
    } catch {
      setErrorMsg('تعذر الاتصال بالخادم، تحقق من اتصالك.');
    } finally {
      setSubmitting(false);
    }
  }, [category, targetName, title, description, severity]);

  const btnBase = {
    position: 'fixed',
    bottom: '100px',
    left: '20px',
    zIndex: 8500,
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'radial-gradient(circle at 50% 0%, rgba(40,227,218,0.35) 0%, rgba(8,20,35,0.95) 70%)',
    border: '1.5px solid rgba(40,227,218,0.5)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.5), 0 0 14px rgba(40,227,218,0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
  };

  return (
    <>
      {             }
      <button
        type="button"
        onClick={openModal}
        title="الإبلاغ عن مشكلة"
        style={btnBase}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 10px 32px rgba(0,0,0,0.6), 0 0 22px rgba(40,227,218,0.3)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.5), 0 0 14px rgba(40,227,218,0.18)';
        }}
      >
        {               }
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M4 4.5V21M4 4.5C4 4.5 7 3 10 3C13 3 15 5 18 5C19.5 5 21 4.5 21 4.5V14.5C21 14.5 19.5 15 18 15C15 15 13 13 10 13C7 13 4 14.5 4 14.5V4.5Z"
            stroke="#28E3DA"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {                   }
      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9990,
            background: 'rgba(0,0,0,0.68)', backdropFilter: 'blur(5px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}
          dir="rtl"
          onClick={e => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div
            style={{
              width: '100%', maxWidth: '520px',
              borderRadius: '28px',
              background: 'radial-gradient(55% 90% at 50% -10%, rgba(40,227,218,0.15) 0%, rgba(40,227,218,0.04) 45%, rgba(5,11,21,0.99) 100%)',
              border: '1px solid rgba(40,227,218,0.28)',
              boxShadow: '0 24px 70px rgba(0,0,0,0.75), 0 0 40px rgba(40,227,218,0.06)',
              padding: '24px',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            {step === 'success' ? (
              <SuccessView onClose={closeModal} />
            ) : (
              <FormView
                isAuthenticated={isAuthenticated}
                category={category}       setCategory={setCategory}
                targetName={targetName}   setTargetName={setTargetName}
                title={title}             setTitle={setTitle}
                description={description} setDescription={setDescription}
                severity={severity}       setSeverity={setSeverity}
                submitting={submitting}
                errorMsg={errorMsg}
                onSubmit={submit}
                onClose={closeModal}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}


function FormView({
  isAuthenticated,
  category, setCategory,
  targetName, setTargetName,
  title, setTitle,
  description, setDescription,
  severity, setSeverity,
  submitting, errorMsg,
  onSubmit, onClose,
}) {
  const showTarget = category === 'lecturer' || category === 'course';
  const targetLabel = category === 'lecturer' ? 'اسم المحاضر (اختياري)' : 'اسم المقرر (اختياري)';
  const targetPlaceholder = category === 'lecturer' ? 'مثال: د. محمد الأحمد' : 'مثال: مقرر البرمجة 101';

  const inputStyle = {
    width: '100%',
    borderRadius: '14px',
    background: 'rgba(8,20,35,0.85)',
    border: '1px solid rgba(255,255,255,0.13)',
    padding: '10px 14px',
    fontSize: '14px',
    color: '#fff',
    fontFamily: 'Alexandria, sans-serif',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '12px',
    color: '#94a3b8',
    fontFamily: 'Alexandria, sans-serif',
  };

  return (
    <>
      {            }
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <button
          type="button"
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '4px', borderRadius: '8px', display: 'flex', alignItems: 'center' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '17px', fontWeight: 700, color: '#f1f5f9', fontFamily: 'Alexandria, sans-serif' }}>
            الإبلاغ عن مشكلة
          </span>
          <div style={{
            width: 36, height: 36, borderRadius: '11px',
            background: 'rgba(40,227,218,0.12)',
            border: '1px solid rgba(40,227,218,0.28)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M4 4.5V21M4 4.5C4 4.5 7 3 10 3C13 3 15 5 18 5C19.5 5 21 4.5 21 4.5V14.5C21 14.5 19.5 15 18 15C15 15 13 13 10 13C7 13 4 14.5 4 14.5V4.5Z"
                stroke="#28E3DA" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      {                  }
      {!isAuthenticated && (
        <div style={{
          marginBottom: '16px',
          padding: '10px 14px',
          borderRadius: '14px',
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.3)',
          color: '#fcd34d',
          fontSize: '13px',
          fontFamily: 'Alexandria, sans-serif',
        }}>
          يجب تسجيل الدخول أولاً لإرسال بلاغ.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {                }
        <div>
          <span style={labelStyle}>نوع البلاغ</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {CATEGORY_OPTIONS.map(opt => {
              const active = category === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setCategory(opt.value)}
                  style={{
                    padding: '9px 6px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 700,
                    fontFamily: 'Alexandria, sans-serif',
                    cursor: 'pointer',
                    border: active ? '1px solid transparent' : '1px solid rgba(255,255,255,0.12)',
                    background: active ? 'linear-gradient(135deg,#28E3DA,#7DF9E8)' : 'rgba(255,255,255,0.04)',
                    color: active ? '#081423' : '#cbd5e1',
                    boxShadow: active ? '0 0 16px rgba(40,227,218,0.4)' : 'none',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {                          }
        {showTarget && (
          <div>
            <label style={labelStyle}>{targetLabel}</label>
            <input
              type="text"
              value={targetName}
              onChange={e => setTargetName(e.target.value.slice(0, 200))}
              placeholder={targetPlaceholder}
              style={inputStyle}
              onFocus={e  => { e.target.style.borderColor = 'rgba(40,227,218,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(40,227,218,0.1)'; }}
              onBlur={e   => { e.target.style.borderColor = 'rgba(255,255,255,0.13)'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
        )}

        {                   }
        <div>
          <label style={labelStyle}>
            عنوان المشكلة <span style={{ color: '#f87171' }}>*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value.slice(0, 200))}
            placeholder="وصف مختصر للمشكلة..."
            style={inputStyle}
            onFocus={e  => { e.target.style.borderColor = 'rgba(40,227,218,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(40,227,218,0.1)'; }}
            onBlur={e   => { e.target.style.borderColor = 'rgba(255,255,255,0.13)'; e.target.style.boxShadow = 'none'; }}
          />
        </div>

        {              }
        <div>
          <label style={{ ...labelStyle, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#64748b', fontFamily: 'Alexandria' }}>{description.length}/2000</span>
            <span>تفاصيل المشكلة <span style={{ color: '#f87171' }}>*</span></span>
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value.slice(0, 2000))}
            rows={4}
            placeholder="اشرح المشكلة بالتفصيل: متى حدثت؟ كيف أثرت عليك؟..."
            style={{ ...inputStyle, resize: 'vertical', minHeight: '100px', lineHeight: 1.6 }}
            onFocus={e  => { e.target.style.borderColor = 'rgba(40,227,218,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(40,227,218,0.1)'; }}
            onBlur={e   => { e.target.style.borderColor = 'rgba(255,255,255,0.13)'; e.target.style.boxShadow = 'none'; }}
          />
        </div>

        {                   }
        <div>
          <span style={labelStyle}>مستوى الأهمية</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {SEVERITY_OPTIONS.map(opt => {
              const active = severity === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSeverity(opt.value)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '9px 12px', borderRadius: '12px', cursor: 'pointer',
                    background: active ? `rgba(${opt.dot === '#f87171' ? '248,113,113' : opt.dot === '#fcd34d' ? '252,211,77' : '147,197,253'}, 0.1)` : 'rgba(255,255,255,0.03)',
                    border: active ? `1px solid ${opt.dot}66` : '1px solid rgba(255,255,255,0.08)',
                    transition: 'all 0.15s ease',
                    textAlign: 'right',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, textAlign: 'right', gap: '1px' }}>
                    <span style={{ fontSize: '13px', color: active ? '#f1f5f9' : '#94a3b8', fontFamily: 'Alexandria', fontWeight: active ? 600 : 400 }}>
                      {opt.desc}
                    </span>
                  </div>
                  <span style={{
                    fontSize: '12px', fontWeight: 700, color: active ? opt.dot : '#64748b',
                    fontFamily: 'Alexandria', minWidth: 40, textAlign: 'right',
                  }}>
                    {opt.label}
                  </span>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: active ? opt.dot : 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
                </button>
              );
            })}
          </div>
        </div>

        {               }
        {errorMsg && (
          <div style={{
            padding: '10px 14px', borderRadius: '12px',
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            color: '#fca5a5', fontSize: '13px', fontFamily: 'Alexandria, sans-serif',
          }}>
            {errorMsg}
          </div>
        )}
      </div>

      {           }
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          style={{
            padding: '10px 18px', borderRadius: '12px', cursor: 'pointer',
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
            color: '#cbd5e1', fontSize: '13px', fontFamily: 'Alexandria, sans-serif',
            opacity: submitting ? 0.5 : 1,
          }}
        >
          إلغاء
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting || !isAuthenticated}
          style={{
            padding: '10px 22px', borderRadius: '12px', cursor: submitting || !isAuthenticated ? 'not-allowed' : 'pointer',
            background: 'linear-gradient(135deg,#28E3DA,#7DF9E8)',
            border: 'none', color: '#081423',
            fontSize: '13px', fontWeight: 700, fontFamily: 'Alexandria, sans-serif',
            opacity: submitting || !isAuthenticated ? 0.55 : 1,
            boxShadow: submitting || !isAuthenticated ? 'none' : '0 4px 16px rgba(40,227,218,0.35)',
            transition: 'opacity 0.15s, box-shadow 0.15s',
          }}
        >
          {submitting ? 'جاري الإرسال...' : 'إرسال البلاغ'}
        </button>
      </div>
    </>
  );
}


function SuccessView({ onClose }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '18px', padding: '20px 0', textAlign: 'center' }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        background: 'rgba(74,222,128,0.12)',
        border: '2px solid rgba(74,222,128,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
          <path d="M5 12l5 5L20 7" stroke="#4ade80" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      <div>
        <h2 style={{ margin: 0, marginBottom: 8, fontSize: '18px', fontWeight: 700, color: '#f1f5f9', fontFamily: 'Alexandria, sans-serif' }}>
          تم إرسال البلاغ بنجاح
        </h2>
        <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8', lineHeight: 1.7, fontFamily: 'Alexandria, sans-serif', maxWidth: 320 }}>
          شكراً لك! وصل بلاغك إلى الإدارة وسيتم مراجعته والتعامل معه في أقرب وقت.
        </p>
      </div>

      <button
        type="button"
        onClick={onClose}
        style={{
          padding: '10px 28px', borderRadius: '14px', cursor: 'pointer',
          background: 'linear-gradient(135deg,#28E3DA,#7DF9E8)',
          border: 'none', color: '#081423',
          fontSize: '14px', fontWeight: 700, fontFamily: 'Alexandria, sans-serif',
          boxShadow: '0 4px 16px rgba(40,227,218,0.35)',
          marginTop: 4,
        }}
      >
        إغلاق
      </button>
    </div>
  );
}
